import { Nullable } from '@jamesgmarks/utilities';
import { convertQueryCommandToQueryString, Equal, In, Not } from '@llws/dynamic-router-utils';
import { IBuildings, ICityRegions, IGeoCities } from '@llws/lift-entity-interfaces';
import { ActionContext } from 'vuex';
import {
  IApiResponse, IMetaResponse,
} from '@/shared';
import { multiFamilyBuildingTypeIds } from '@/shared/helpers/multi-family-building-type';
import { adminRequest } from '@/shared/request-configs/admin-request';
import cities from '@/shared/services/cities';
import clients from '@/shared/services/clients';
import { TRootState } from '..';
import { hasPromotionAccessFor } from '@/shared/helpers/promotions-config';

interface ICityState {
  list: Nullable<IApiResponse<Required<IGeoCities>[], IMetaResponse>> | IApiResponse<IGeoCities[], IMetaResponse>;
  single: Nullable<IApiResponse<Required<IGeoCities>, IMetaResponse>> | IApiResponse<IGeoCities, IMetaResponse>;
  regions: Nullable<IApiResponse<Required<ICityRegions>[], IMetaResponse>> | IApiResponse<ICityRegions[], IMetaResponse>;
  clientCities: Nullable<IApiResponse<Required<IGeoCities>[], IMetaResponse>> | IApiResponse<IGeoCities[], IMetaResponse>;
}

const initialState: ICityState = {
  list: null,
  single: null,
  regions: null,
  clientCities: null,
};

export default {
  namespaced: true, // 'cities/'
  state: initialState,
  getters: {
    list: (state: ICityState) => [ ...state.list?.data || [] ],

    regions: (state: ICityState) => [ ...state.regions?.data || [] ],

    clientCities: (state: ICityState) => [ ...state.clientCities?.data || [] ],

    /**
     * A permission filter passthrough for city property data.
     * If user has no permissions for properties within a city,
     * the city itself is removed from the filtered list.
     */
    permittedPropertiesByCity: (state: ICityState) => {
      if (!state.list?.data) {
        return [];
      }

      const permittedPropertiesByCity = state.list.data.map((c) => ({
        ...c,
        buildings: c.buildings?.filter((b) => hasPromotionAccessFor(b.id, 'update')) ?? [],
      }));

      return permittedPropertiesByCity.filter((c) => (c.buildings ?? []).length > 0);
    },
  },
  mutations: {
    set: (state: ICityState, payload: IApiResponse<IGeoCities[], IMetaResponse>) => {
      state.list = payload;
    },

    setRegions: (state: ICityState, payload: IApiResponse<ICityRegions[], IMetaResponse>) => {
      state.regions = payload;
    },

    setClientCities: (state: ICityState, payload: IApiResponse<IGeoCities[], IMetaResponse>) => {
      state.clientCities = payload;
    },

    reset: (state: ICityState) => {
      state.list = null;
      state.single = null;
    },
  },
  actions: {
    get: async (ctx: ActionContext<ICityState, TRootState>, searchValue: string = '') => {
      const [ cityValue, provinceValue ] = searchValue.split(',');
      const result = await cities.get((cityValue || '').trim(), (provinceValue || '').trim());
      ctx.commit('set', result);
    },

    getRegionsByCityAndClient: async (ctx: ActionContext<ICityState, TRootState>, {
      clientId, cityId,
    } : { clientId: number, cityId: number }) => {
      const result = await cities.getRegionsByCityAndClient(clientId, cityId);
      ctx.commit('setRegions', result);
    },

    getRegionsByCityOfferedAndClient: async (ctx: ActionContext<ICityState, TRootState>, {
      cityOfferedId,
    } : { clientId?: number, cityOfferedId: number }) => {
      const result = await cities.getRegionsByCityOfferedAndClient(cityOfferedId);
      return result;
    },

    getClientCities: async (ctx: ActionContext<ICityState, TRootState>, { clientId } : { clientId: number }) => {
      const result = await clients.getClientCities(clientId);
      ctx.commit('setClientCities', result);
    },

    getMultiFamilyClientCities: async (ctx: ActionContext<ICityState, TRootState>, { clientId } : { clientId: number }) => {
      const result = await clients.getClientCities(clientId);
      const q = convertQueryCommandToQueryString({
        alias: 'property',
        where: [
          Not(Equal(`property.status`, 'deleted')),
          Not(Equal(`property.status`, 'disabled')),
          {
            condition: 'and',
            whereCommands: [
              In('property.buildingTypeId', multiFamilyBuildingTypeIds),
            ],
          } ],
        select: ['property.buildingName', 'property.id', 'property.cityId'],
      });
      const properties = await adminRequest.get(`/property-api/properties/${q}`);
      const cityIds = (properties.data ?? []).map((item: IBuildings) => ((item && item.cityId) ? (item.cityId) : null));
      result.data = result.data && result.data.filter((city) => cityIds.indexOf(city.id) !== -1);
      ctx.commit('setClientCities', result);
    },

    getCityProperties: async (ctx: ActionContext<ICityState, TRootState>) => {
      const result = await cities.getCityProperties();
      ctx.commit('set', result);
    },

    async saveRegion(ctx: ActionContext<ICityState, TRootState>, payload: any) {
      await adminRequest.post('/property-api/regions', payload);
    },

    async removeRegion(ctx: ActionContext<ICityState, TRootState>, payload: any) {
      await adminRequest.delete('/property-api/regions', [payload]);
    },
  },
};
